import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { getDateLocale } from '../utils/dateLocale';
import TimetableCard from './TimetableCard';
import FeedbackButtons from './FeedbackButtons';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isCrisis?: boolean;
  isStreaming?: boolean;
}

interface MessageBubbleProps {
  message: Message;
  sessionId?: string;
  precedingUserMessage?: string;
  /** Demo Mode: feedback buttons render but do not call the API. */
  demo?: boolean;
}

export default function MessageBubble({ message, sessionId, precedingUserMessage, demo = false }: MessageBubbleProps) {
  const { t } = useTranslation('chat');
  const { preferredLanguage } = useAuth();
  const dateLocale = getDateLocale(preferredLanguage);
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`
          max-w-[85%] md:max-w-[75%] rounded-2xl px-4 py-3
          ${
            isUser
              ? 'bg-uel-purple-600 text-white'
              : message.isCrisis
                ? 'bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800'
                : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
          }
        `}
      >
        {!isUser && (
          <div className="flex items-center space-x-2 mb-2">
            <img
              src="/uel-crest.webp"
              alt=""
              aria-hidden="true"
              className="w-6 h-6 object-contain"
            />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('pailaLabel')}
            </span>
          </div>
        )}

        <div
          className={`
            message-content
            ${isUser ? 'text-white' : 'text-gray-900 dark:text-white'}
          `}
        >
          {message.isStreaming && !message.content && preferredLanguage !== 'en' ? (
            <p className="mb-0 text-gray-500 dark:text-gray-400 italic">
              {t('translatingResponse')}
              <span className="inline-block w-2 h-4 ml-1 bg-uel-purple-600 dark:bg-uel-purple-400 animate-pulse rounded-sm" aria-hidden="true" />
            </p>
          ) : (
            <>
              <ReactMarkdown
                components={{
                  // Custom renderers for specific content
                  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc ml-4 mb-2">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal ml-4 mb-2">{children}</ol>,
                  li: ({ children }) => <li className="mb-1">{children}</li>,
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`underline ${
                        isUser
                          ? 'text-white'
                          : 'text-uel-purple-600 dark:text-uel-purple-400'
                      }`}
                    >
                      {children}
                      <span className="sr-only"> {t('opensInNewTab')}</span>
                    </a>
                  ),
                  code: ({ children }) => (
                    <code
                      className={`px-1 py-0.5 rounded text-sm font-mono ${
                        isUser
                          ? 'bg-uel-purple-700'
                          : 'bg-gray-100 dark:bg-gray-700'
                      }`}
                    >
                      {children}
                    </code>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
              {message.isStreaming && (
                <span className="inline-block w-2 h-4 ml-0.5 bg-uel-purple-600 dark:bg-uel-purple-400 animate-pulse rounded-sm" aria-hidden="true" />
              )}
            </>
          )}
        </div>

        {/* Timestamp and Feedback */}
        <div className="flex items-center justify-between mt-2">
          <div
            className={`
              text-xs
              ${isUser ? 'text-uel-purple-200' : 'text-gray-400 dark:text-gray-500'}
            `}
          >
            {format(message.timestamp, 'h:mm a', { locale: dateLocale })}
          </div>
          {!isUser && sessionId && (
            <FeedbackButtons
              messageId={message.id}
              sessionId={sessionId}
              userMessage={precedingUserMessage || ''}
              assistantMessage={message.content}
              demo={demo}
            />
          )}
        </div>
      </div>
    </div>
  );
}
